import { Component } from 'react';
import styled from 'styled-components';
import { withRouter } from 'react-router-dom';
import uniqueId from 'transmute/uniqueId';
import partition from 'hs-lodash/partition';
import memoize from 'transmute/memoize';
import uniqueIdForObject from 'automation-ui-core/utils/uniqueIdForObject';
import { BATTLESHIP, CALYPSO, OZ_DARK } from 'HubStyleTokens/colors';
import { BUFFER_MARGIN } from 'automation-ui-flow-core/canvas/graphConstants';
import calculateNodePositions from 'automation-ui-directed-graph/utils/calculateNodePositions';
import shouldGoToPathRender from 'automation-ui-directed-graph/utils/shouldGoToPathRender';
import GoToPath from 'automation-ui-directed-graph/GoToPath';
import Rectangle from 'automation-ui-directed-graph/Rectangle';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const StyledSVG = styled.svg.withConfig({
  displayName: "BezierArrowsSVG__StyledSVG",
  componentId: "sc-4mbtg2-0"
})(["position:absolute;top:0;left:0;width:0;height:0;z-index:10;pointer-events:none;background:transparent;"]);
const MARKER_WIDTH = 20;
const MARKER_HEIGHT = 15;
const DEFAULT_LINE_STROKE_WIDTH = 3;
const BOLDED_LINE_STROKE_MULTIPLIER = 1.5;
class BezierArrowsSVG extends Component {
  constructor(props) {
    super(props);
    this.getPaths = memoize(() => {
      const {
        goToPaths,
        scrollElementClientRect,
        lineStrokeWidth = DEFAULT_LINE_STROKE_WIDTH
      } = this.props;
      let paths = Object.entries(goToPaths).filter(([, {
        toNodeId
      }]) => !!toNodeId).map(([fromNodeId, {
        toNodeId,
        isHover,
        isGoToEditNode
      }]) => new GoToPath({
        points: this.drawCurvedLine(fromNodeId, toNodeId),
        isHover,
        isGoToEditNode,
        pathId: `${fromNodeId}-path-${toNodeId}`,
        lineStrokeWidth: isHover || isGoToEditNode ? lineStrokeWidth * BOLDED_LINE_STROKE_MULTIPLIER : lineStrokeWidth,
        markerWidth: MARKER_WIDTH,
        markerHeight: MARKER_HEIGHT
      }));
      const {
        width,
        height
      } = scrollElementClientRect;
      const maxCurveLength = Math.hypot(width + BUFFER_MARGIN * 2, height + BUFFER_MARGIN * 2);
      paths = paths.map(path => {
        const roughCurveLength = Math.hypot(path.bounds.width, path.bounds.height);
        const numberOfSegments = Math.max(1, Math.round(roughCurveLength / maxCurveLength));
        return path.split(numberOfSegments);
      }).flat();
      return paths;
    }, () => {
      const {
        goToPaths,
        shouldVirtuallyRenderArrows,
        scrollElementClientRect = new Rectangle(),
        lineStrokeWidth,
        directedGraph,
        nodeHeights
      } = this.props;
      const nodePositions = calculateNodePositions(directedGraph, nodeHeights);
      return `${uniqueIdForObject(goToPaths)}-${uniqueIdForObject(nodePositions)}-${shouldVirtuallyRenderArrows}-${scrollElementClientRect.width}-${scrollElementClientRect.height}-${lineStrokeWidth}`;
    });
    this.drawCurvedLine = (fromNodeId, toNodeId) => {
      var _nodeHeights$get;
      const {
        nodeHeights,
        directedGraph,
        nodeWidth
      } = this.props;
      const nodePositions = calculateNodePositions(directedGraph, nodeHeights);
      let {
        x: p0x,
        y: p0y
      } = nodePositions.get(fromNodeId) || {
        x: 0,
        y: 0
      };
      let {
        x: p3x,
        y: p3y
      } = nodePositions.get(toNodeId) || {
        x: 0,
        y: 0
      };
      p0x += nodeWidth / 2;
      p0y += (_nodeHeights$get = nodeHeights.get(`node-${fromNodeId}`)) !== null && _nodeHeights$get !== void 0 ? _nodeHeights$get : directedGraph.getDefaultNodeHeight(fromNodeId);
      p3x += nodeWidth / 2 - 1;
      p3y -= 1;
      const constant_delta = 200;
      const deltaY = Math.abs(p3y - p0y);
      let deltaHY1Flip = 1;
      let deltaHY2Flip = -1;
      let deltaHX1Flip = -1;
      let deltaHX2Flip = 1;
      if (p0x <= p3x) {
        if (p0y <= p3y) {
          deltaHX1Flip = constant_delta;
          deltaHX2Flip = constant_delta * -1;
          deltaHY1Flip = deltaY;
          deltaHY2Flip = constant_delta * -1;
        } else {
          deltaHX1Flip = constant_delta;
          deltaHX2Flip = constant_delta * -1;
          deltaHY1Flip = constant_delta;
          deltaHY2Flip = constant_delta * -1;
        }
      } else {
        if (p0y <= p3y) {
          deltaHX1Flip = constant_delta * -1;
          deltaHX2Flip = constant_delta;
          deltaHY1Flip = deltaY;
          deltaHY2Flip = constant_delta * -1;
        } else {
          deltaHX1Flip = constant_delta * -1;
          deltaHX2Flip = constant_delta;
          deltaHY1Flip = constant_delta;
          deltaHY2Flip = constant_delta * -1;
        }
      }
      const p1x = p0x + deltaHX1Flip;
      const p1y = p0y + deltaHY1Flip;
      const p2x = p3x + deltaHX2Flip;
      const p2y = p3y + deltaHY2Flip;
      return {
        p0x,
        p0y,
        p1x,
        p1y,
        p2x,
        p2y,
        p3x,
        p3y
      };
    };
    this.shouldRenderPath = path => {
      const {
        shouldVirtuallyRenderArrows,
        canvasLeftScroll,
        canvasTopScroll,
        scrollElementClientRect,
        zoomLevel
      } = this.props;
      return !shouldVirtuallyRenderArrows || shouldGoToPathRender({
        path,
        zoomLevel,
        canvasLeftScroll: canvasLeftScroll,
        canvasTopScroll: canvasTopScroll,
        scrollElementClientRect: scrollElementClientRect
      });
    };
    this.getSVGPathsInOrder = () => {
      const paths = this.getPaths();
      const [boldedPaths, nonBoldedPaths] = partition(paths.filter(this.shouldRenderPath), path => path.isHover || path.isGoToEditNode);
      return nonBoldedPaths.concat(boldedPaths);
    };
    this.isPathHighlighted = pathId => {
      var _highlightedGotoPaths;
      const {
        highlightedGotoPaths
      } = this.props;
      const currentGotoPath = pathId.split('-path-')[0];
      return (_highlightedGotoPaths = highlightedGotoPaths === null || highlightedGotoPaths === void 0 ? void 0 : highlightedGotoPaths.has(currentGotoPath)) !== null && _highlightedGotoPaths !== void 0 ? _highlightedGotoPaths : false;
    };
    this.renderPath = ({
      pathId,
      isGoToEditNode,
      isHover,
      path,
      bounds: {
        minX,
        minY,
        width,
        height
      },
      shouldShowEndMarker
    }) => {
      const {
        lineStrokeWidth = DEFAULT_LINE_STROKE_WIDTH,
        lineColor
      } = this.props;
      const isPathHighlighted = this.isPathHighlighted(pathId);
      const isBolded = isGoToEditNode || isHover || isPathHighlighted;
      const boldedColor = isPathHighlighted ? OZ_DARK : CALYPSO;
      const boldedMarkerUrl = isPathHighlighted ? this.highlightedMarkerUrl : this.boldMarkerUrl;
      const markerUrl = isBolded ? boldedMarkerUrl : this.markerUrl;
      const pathProps = {
        d: path,
        strokeDasharray: isBolded ? '9,6' : '6,4',
        strokeWidth: isBolded ? lineStrokeWidth * BOLDED_LINE_STROKE_MULTIPLIER : lineStrokeWidth,
        markerEnd: shouldShowEndMarker ? markerUrl : undefined,
        fill: 'none',
        stroke: isBolded ? boldedColor : lineColor
      };
      return /*#__PURE__*/_jsx(StyledSVG, {
        "data-test-id": pathId,
        style: {
          left: `${minX}px`,
          top: `${minY}px`,
          width: `${width}px`,
          height: `${height}px`
        },
        viewBox: `${minX} ${minY} ${width} ${height}`,
        children: /*#__PURE__*/_jsx("path", Object.assign({}, pathProps))
      }, `path-${pathId}`);
    };
    this.markerId = uniqueId('marker-');
    this.markerUrl = `url(#${this.markerId})`;
    this.boldMarkerId = uniqueId('bold-marker-');
    this.boldMarkerUrl = `url(#${this.boldMarkerId})`;
    this.highlightedMarkerId = uniqueId('highlighted-marker-');
    this.highlightedMarkerUrl = `url(#${this.highlightedMarkerId})`;
  }

  /**
   * Since the bolded arrow should be rendered at the top of all other arrows,
   * this function arranges the bolded paths at the end of the array of paths.
   */

  render() {
    const {
      lineColor
    } = this.props;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(StyledSVG, {
        children: /*#__PURE__*/_jsxs("defs", {
          children: [/*#__PURE__*/_jsx("marker", {
            id: this.markerId,
            orient: "auto",
            markerWidth: MARKER_WIDTH,
            markerHeight: MARKER_HEIGHT,
            refX: "4",
            refY: "3",
            children: /*#__PURE__*/_jsx("path", {
              d: "M0,0 V5 L5,2 Z",
              fill: lineColor
            })
          }), /*#__PURE__*/_jsx("marker", {
            id: this.boldMarkerId,
            orient: "auto",
            markerWidth: MARKER_WIDTH,
            markerHeight: MARKER_HEIGHT,
            refX: "4",
            refY: "3",
            children: /*#__PURE__*/_jsx("path", {
              d: "M0,0 V5 L5,2 Z",
              fill: CALYPSO
            })
          }), /*#__PURE__*/_jsx("marker", {
            id: this.highlightedMarkerId,
            orient: "auto",
            markerWidth: MARKER_WIDTH,
            markerHeight: MARKER_HEIGHT,
            refX: "4",
            refY: "3",
            children: /*#__PURE__*/_jsx("path", {
              d: "M0,0 V5 L5,2 Z",
              fill: OZ_DARK
            })
          })]
        })
      }), this.getSVGPathsInOrder().map(this.renderPath)]
    });
  }
}

// the router subscription is needed to re-render on route changes
BezierArrowsSVG.defaultProps = {
  lineStrokeWidth: DEFAULT_LINE_STROKE_WIDTH,
  lineColor: BATTLESHIP,
  scrollElementClientRect: new Rectangle()
};
export default withRouter(BezierArrowsSVG);